import React, { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useI18n, useButton } from '~Hooks';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import { Box } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import BackIcon from '~Assets/icons/arrow_back.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import { BackButton, Title } from '~ComponentsImproved/BackTopBar';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

interface Props {
  progressText: string;
  progress: number;
}

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const Wrapper = styled(Box)`
  height: 100vh;
  position: relative;
`;

const TopBar = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 240px;
  height: 22px;
  border-radius: 20px;
  color: var(--colorUIGreen);
  border: 2px solid;
  position: relative;
  margin-top: 40px;

  &::before {
    content: '';
    position: absolute;
    margin: 2px;
    inset: 0 ${({ progress }) => 100 - progress}% 0 0;
    border-radius: inherit;
    background: currentColor;
    transition: inset 0.5s ease-out;
  }
`;

const LogoContainer = styled.div`
  width: 120px;
  height: 120px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/** Initial loader for EPOS */
const InitialLoader: FC<Props> = ({ progressText, progress }) => {
  const { i18n } = useI18n();
  const history = useHistory();
  const [currentText, setCurrentText] = useState(progressText);

  const handleBack = () => {
    history.push('/venues');
  };

  const backButton = useButton({
    onClick: handleBack,
  });

  useEffect(() => {
    if (progressText !== currentText) {
      setCurrentText(progressText);
    }
  }, [progressText, currentText]);

  return (
    <Wrapper display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      <TopBar display="flex" alignItems="center" padding="medium">
        <BackButton {...backButton}>
          <Icon src={BackIcon} color="var(--colorBlueGray1200)" />
        </BackButton>
        <Title dark={false}>{i18n('SSK.Return')}</Title>
      </TopBar>

      <LogoContainer>
        <Logo src={bLogo} alt="Baemingo Logo" />
      </LogoContainer>

      <ProgressBar progress={progress} />

      <Heading level={3} color="var(--colorBlueGray1200)" top={16}>
        {currentText}
      </Heading>
    </Wrapper>
  );
};

export default InitialLoader;
